import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { HardwareFieldDefinition } from '../../services/typesService';
import { getTypeBadgeColor } from '../../utils/fieldTypes';
import { FieldErrors, FieldValues, SchemaGroup, parseOptions } from './templateSchema';

/** The device-password row: random per device, or a value typed here. */
export interface PasswordRowProps {
  generate: boolean;
  value: string;
  placeholder: string;
  onGenerateChange: (generate: boolean) => void;
  onValueChange: (value: string) => void;
}

interface TemplateFieldsEditorProps {
  groups: SchemaGroup[];
  values: FieldValues;
  errors: FieldErrors;
  disabled?: boolean;
  onChange: (fieldName: string, value: string) => void;
  /** Present when the gateway schema has a `password` field. */
  password?: PasswordRowProps;
}

/** One editable row of a template: name, kind chip, the control for its type. */
const TemplateFieldRow: React.FC<{
  def: HardwareFieldDefinition;
  value: string;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ def, value, error, disabled, onChange }) => {
  const { t } = useI18n();
  const placeholder = def.placeholder || '—';

  let control: React.ReactNode;
  switch (def.value_type) {
    case 'boolean':
      control = (
        <label className="cu-check tpd-cell-check">
          <input type="checkbox" disabled={disabled} checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
          <span className="cu-hint">{value === 'true' ? t('common.yes') : t('common.no')}</span>
        </label>
      );
      break;
    case 'password':
      control = <input type="password" aria-label={def.field_name} className="cu-input mono" value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'port':
    case 'number':
      control = <input type="number" aria-label={def.field_name} className="cu-input mono" value={value} placeholder={placeholder} disabled={disabled} min={def.min_value ?? undefined} max={def.max_value ?? undefined} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'dropdown':
      control = (
        <select aria-label={def.field_name} className="cu-input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          <option value="">{placeholder}</option>
          {parseOptions(def.options).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
      break;
    default:
      control = <input type="text" aria-label={def.field_name} className="cu-input mono" value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <tr className={error ? 'row-invalid' : ''}>
      <td>
        <code>{def.field_name}</code>
        {def.display_name && def.display_name !== def.field_name && <span className="tpd-field-label">{def.display_name}</span>}
      </td>
      <td><span className="type-badge tpd-type" data-kind={def.value_type} style={{ ['--kind-color' as any]: getTypeBadgeColor(def.value_type) }}>{def.value_type}</span></td>
      <td>{control}{error && <div className="cu-error">{error}</div>}</td>
    </tr>
  );
};

/**
 * The editable values of a template, one table per schema group, plus
 * the device-password row when the gateway has one. Used by the create
 * dialog and by the detail dialog in edit mode.
 */
export const TemplateFieldsEditor: React.FC<TemplateFieldsEditorProps> = ({ groups, values, errors, disabled, onChange, password }) => {
  const { t } = useI18n();
  const head = (
    <thead>
      <tr>
        <th>{t('devices.fieldName')}</th>
        <th>{t('common.type')}</th>
        <th>{t('common.value')}</th>
      </tr>
    </thead>
  );

  return (
    <div className="field-defs-list tpd-groups">
      {groups.map((group) => (
        <div className="field-group tpd-group" key={group.name}>
          <h4 className="group-header tpd-group-title">{group.name}<span className="ud-tab-count">{group.fields.length}</span></h4>
          <table className="field-defs-table tpd-table tpd-table-edit">
            {head}
            <tbody>
              {group.fields.map((def) => (
                <TemplateFieldRow
                  key={def.id}
                  def={def}
                  value={values[def.field_name] ?? ''}
                  error={errors[def.field_name]}
                  disabled={disabled}
                  onChange={(value) => onChange(def.field_name, value)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {password && (
        <div className="field-group tpd-group">
          <h4 className="group-header tpd-group-title">{t('configTemplates.devicePassword')}</h4>
          <table className="field-defs-table tpd-table tpd-table-edit">
            {head}
            <tbody>
              <tr>
                <td><code>generate_password</code></td>
                <td><span className="type-badge tpd-type" data-kind="boolean">boolean</span></td>
                <td>
                  <label className="cu-check tpd-cell-check">
                    <input type="checkbox" checked={password.generate} disabled={disabled} onChange={(e) => password.onGenerateChange(e.target.checked)} />
                    <span className="cu-hint">{password.generate ? t('configTemplates.randomPerDevice') : t('common.no')}</span>
                  </label>
                </td>
              </tr>
              {!password.generate && (
                <tr>
                  <td><code>password</code></td>
                  <td><span className="type-badge tpd-type" data-kind="password">password</span></td>
                  <td>
                    <input type="text" className="cu-input mono" value={password.value} disabled={disabled} onChange={(e) => password.onValueChange(e.target.value)} placeholder={password.placeholder} maxLength={100} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
